import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { GrClose } from "react-icons/gr";
import ZephrFeature from "components/zephr";
import { ZEPHR_FEATURES } from "lib/zephr/constants";
import { zephrModal } from "lib/zephr/modal";
import { useUserData } from "lib/store/user";
import { hasIncompleteProfile } from "lib/zephr/complete-profile-fields";
import { useReserveScrollbarWidth } from "lib/hooks/useReserveScrollbarWidth";
import {
  COMPLETE_PROFILE_PATH,
  COMPLETE_PROFILE_TRIGGER_SELECTOR,
  getCurrentLogin,
  isPromptEligibleUser,
  isPromptSkippedForCurrentLogin,
} from "lib/zephr/complete-profile-prompt";
import CompleteProfileModal, {
  DIALOG_ID,
} from "components/zephr/CompleteProfileModal";

const ONBOARDING_TRIGGER_SELECTOR = '[data-show-onboarding="true"]';

const EVALUATION_WINDOW_MS = 30_000;
const EVALUATION_INTERVAL_MS = 1000;

export const CompleteProfilePrompt = () => {
  const { userProfile } = useUserData();
  const [showModal, setShowModal] = useState(false);
  const [modalReady, setModalReady] = useState(false);
  const skipRef = useRef<() => void>(() => {});
  const shownRef = useRef(false);
  const loadedLoginRef = useRef(getCurrentLogin());

  useEffect(() => {
    if (shownRef.current) {
      return;
    }

    const shouldPrompt = () => {
      if (!document.querySelector(COMPLETE_PROFILE_TRIGGER_SELECTOR)) {
        return false;
      }

      if (document.querySelector(ONBOARDING_TRIGGER_SELECTOR)) {
        return false;
      }

      if (window.location.pathname === COMPLETE_PROFILE_PATH) {
        return false;
      }

      if (zephrModal.isPending()) {
        return false;
      }

      if (!isPromptEligibleUser(loadedLoginRef.current)) {
        return false;
      }

      if (isPromptSkippedForCurrentLogin()) {
        return false;
      }

      return hasIncompleteProfile({
        ...userProfile.details,
        first_name: userProfile.first_name,
        last_name: userProfile.last_name,
      });
    };

    let observer: MutationObserver | null = null;
    let intervalId: number | undefined;
    let timeoutId: number | undefined;

    const stop = () => {
      observer?.disconnect();
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };

    const evaluate = () => {
      if (shownRef.current) {
        return;
      }

      if (shouldPrompt()) {
        shownRef.current = true;
        setShowModal(true);
        stop();
      }
    };

    evaluate();

    if (!shownRef.current) {
      observer = new MutationObserver(evaluate);
      observer.observe(document.body, { childList: true, subtree: true });
      intervalId = window.setInterval(evaluate, EVALUATION_INTERVAL_MS);
      timeoutId = window.setTimeout(stop, EVALUATION_WINDOW_MS);
    }

    return stop;
  }, [userProfile]);

  const isOpen = showModal && modalReady;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useReserveScrollbarWidth({ modalOpened: isOpen });

  return (
    <>
      <div className={classNames({ hidden: !isOpen })}>
        <div
          className="fixed inset-0 z-50 bg-[#000000a2]"
          data-testid="complete-profile-overlay"
          onClick={() => skipRef.current()}
        />
        <div className="pointer-events-none fixed inset-0 z-[51] flex items-center justify-center">
          <div
            id={DIALOG_ID}
            data-zephr-dialog
            className="pointer-events-auto relative max-h-[85vh] w-[94vw] max-w-[400px] overflow-y-auto rounded-2xl bg-white px-6 pb-8 pt-10 shadow-[0_10px_38px_-10px_rgba(14,18,22,0.35)] md:px-8"
          >
            <button
              aria-label="Close"
              data-testid="complete-profile-close"
              className="absolute right-3 top-3 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black-rock"
              onClick={() => skipRef.current()}
            >
              <GrClose size="1.2em" />
            </button>
            <ZephrFeature name={ZEPHR_FEATURES.COMPLETE_PROFILE_PROMPT} />
          </div>
        </div>
      </div>
      {showModal ? (
        <CompleteProfileModal
          skipRef={skipRef}
          onReady={() => setModalReady(true)}
          onClose={() => {
            setShowModal(false);
            setModalReady(false);
          }}
        />
      ) : null}
    </>
  );
};

export default CompleteProfilePrompt;
